import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Instagram, Link2, Unplug, CheckCircle2, AlertCircle, Clock,
  Send, RefreshCw, ImageIcon, ExternalLink, Loader2, ShieldAlert
} from "lucide-react";
import CaroselloPreviewDemo from "@/components/CaroselloPreviewDemo";
import InstagramProfilePreview from "@/components/InstagramProfilePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER_URL = (fid) => fid ? `${API}/rivoluzione/cover/${fid}` : null;

export default function InstagramPublishTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contents, setContents] = useState([]);
  const [selected, setSelected] = useState([]);  // ordered list of content_ids
  const [caption, setCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publications, setPublications] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        axios.get(`${API}/instagram/status`, { withCredentials: true }),
        axios.get(`${API}/admin/rivoluzione/contenuti`, { withCredentials: true }),
        axios.get(`${API}/instagram/publications`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setContents((c.data || []).filter(x => x.cover_file_id));
      setPublications(p.data || []);
    } catch (e) {
      toast.error("Errore caricamento stato Instagram");
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Leggi ig_status query param dopo callback OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const st = params.get("ig_status");
    if (st === "connected") {
      toast.success(`Account Instagram @${params.get("user") || ""} collegato!`);
      window.history.replaceState({}, "", window.location.pathname);
      refresh();
    } else if (st === "error") {
      toast.error(`Errore collegamento Instagram: ${params.get("ig_msg") || ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refresh]);

  const startOAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/instagram/auth/start`, { withCredentials: true });
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore avvio autorizzazione");
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnettere l'account Instagram? Le pubblicazioni future non saranno piu' possibili.")) return;
    try {
      await axios.post(`${API}/instagram/disconnect`, {}, { withCredentials: true });
      toast.success("Account disconnesso");
      refresh();
    } catch { toast.error("Errore"); }
  };

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : (s.length >= 10 ? s : [...s, id]));
  };

  const publish = async () => {
    if (selected.length < 2) { toast.error("Seleziona almeno 2 immagini per il carosello"); return; }
    if (!caption.trim()) { toast.error("Inserisci una didascalia"); return; }
    if (caption.length > 2200) { toast.error("Didascalia troppo lunga (max 2200 caratteri)"); return; }
    setPublishing(true);
    try {
      const { data } = await axios.post(`${API}/instagram/publish-carousel`,
        { content_ids: selected, caption },
        { withCredentials: true });
      toast.success(`Carosello pubblicato! ID: ${data.media_id}`);
      setSelected([]);
      setCaption("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pubblicazione fallita");
    }
    setPublishing(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0F1F14]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ig-publish-tab">
      {/* ===== Autorizzazione ===== */}
      <div className="bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] rounded-2xl p-6 text-white" data-testid="ig-auth-card">
        <div className="flex items-center gap-2 mb-1">
          <Instagram className="w-5 h-5 text-[#D97B58]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97B58]">Pannello Autorizzazione</p>
        </div>
        <h2 className="font-heading text-2xl font-bold mb-4">Pubblicazione API Instagram</h2>

        {!status?.configured ? (
          <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-200 mb-1">Configurazione Meta mancante</p>
              <p className="text-sm text-white/70 leading-relaxed">
                Per abilitare la pubblicazione automatica su Instagram servono le credenziali <strong>Meta Developer App</strong>.
                Chiedi al tecnico di inserire <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs">META_APP_ID</code> e
                <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs ml-1">META_APP_SECRET</code> nel file <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs">.env</code>.
              </p>
              <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-amber-200 hover:text-amber-100 underline">
                Apri Meta Developers <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ) : status.connected ? (
          <div className="flex items-center justify-between gap-4 bg-white/5 rounded-xl p-4 border border-white/10" data-testid="ig-connected-card">
            <div className="flex items-center gap-3">
              {status.ig_profile_picture_url ? (
                <img src={status.ig_profile_picture_url} alt="" className="w-12 h-12 rounded-full border-2 border-[#D97B58]" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B] flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">@{status.ig_username}</p>
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-xs text-white/60">Pagina: {status.page_name}</p>
                <p className="text-xs text-white/40 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" /> Token scade in {status.days_left} giorni
                  {status.needs_refresh && <span className="text-amber-300 ml-1">— rinnova presto</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={refresh} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={disconnect} className="bg-red-500/10 border-red-400/30 text-red-200 hover:bg-red-500/20" data-testid="ig-disconnect">
                <Unplug className="w-4 h-4 mr-1" /> Disconnetti
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
            <div>
              <p className="font-semibold mb-1 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400" />
                Instagram non collegato
              </p>
              <p className="text-sm text-white/60">
                Autorizza l'account <strong>@colucci_assicuratori</strong> per pubblicare caroselli direttamente dal pannello.
                Serve un account Instagram <strong>Business</strong> o <strong>Creator</strong> collegato a una Pagina Facebook.
              </p>
            </div>
            <Button onClick={startOAuth}
              className="bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B] hover:opacity-90 text-white font-semibold shrink-0" data-testid="ig-connect-btn">
              <Link2 className="w-4 h-4 mr-2" /> Collega Instagram
            </Button>
          </div>
        )}
      </div>

      {/* ===== Pubblicatore carosello (se connesso) ===== */}
      {status?.connected && (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="ig-publisher-card">
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
            <div>
              <h3 className="font-heading text-lg font-bold text-zinc-900 mb-1">Pubblica un carosello</h3>
              <p className="text-sm text-zinc-500">Seleziona 2-10 contenuti del canale come slide del carosello. Le cover devono essere gia' state caricate in "Canale Instagram".</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-heading font-bold text-[#0F1F14]">{selected.length}<span className="text-zinc-300 text-base">/10</span></p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">selezionate</p>
            </div>
          </div>

          {contents.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-zinc-200 rounded-xl">
              <ImageIcon className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">Nessun contenuto con cover disponibile. Crea prima qualche contenuto in "Canale Instagram".</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-5 max-h-[380px] overflow-y-auto p-1" data-testid="ig-content-grid">
                {contents.map(c => {
                  const idx = selected.indexOf(c.id);
                  const isSelected = idx >= 0;
                  return (
                    <button key={c.id} onClick={() => toggleSelect(c.id)}
                      className={`group relative aspect-[4/5] rounded-lg overflow-hidden border-2 transition-all ${isSelected ? "border-[#D97B58] ring-2 ring-[#D97B58]/20" : "border-transparent hover:border-zinc-300"}`}
                      data-testid={`ig-content-${c.id}`}>
                      <img src={COVER_URL(c.cover_file_id)} alt={c.titolo} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <div className={`absolute inset-0 ${isSelected ? "bg-[#0F1F14]/30" : "bg-black/20 opacity-0 group-hover:opacity-100"} transition-opacity`} />
                      {isSelected && (
                        <div className="absolute top-1 left-1 w-6 h-6 rounded-full bg-[#D97B58] text-white text-xs font-bold flex items-center justify-center shadow-lg">
                          {idx + 1}
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-[9px] text-white font-semibold line-clamp-1">{c.titolo}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <label className="text-sm font-medium text-zinc-700 mb-1 block">Didascalia Instagram</label>
              <Textarea rows={5} value={caption} onChange={e => setCaption(e.target.value)}
                placeholder="Scrivi la didascalia. Puoi usare hashtag #assicurazione #news #colucciassicuratori e menzioni @..."
                maxLength={2200} data-testid="ig-caption" />
              <div className="flex items-center justify-between text-xs text-zinc-400 mt-1 mb-4">
                <span>{caption.length}/2200 caratteri</span>
                <span>Max 30 hashtag, 20 menzioni</span>
              </div>

              <div className="flex gap-3 items-center justify-between border-t border-zinc-100 pt-4">
                <p className="text-xs text-zinc-500">
                  Le immagini vengono prelevate dalle cover pubbliche gia' caricate.
                </p>
                <Button onClick={publish} disabled={publishing || selected.length < 2 || !caption.trim()}
                  className="bg-gradient-to-tr from-[#FEDA77] via-[#F58529] to-[#DD2A7B] hover:opacity-90 text-white font-semibold" data-testid="ig-publish-btn">
                  {publishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pubblicazione...</> : <><Send className="w-4 h-4 mr-2" /> Pubblica carosello</>}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== Anteprima Pagina Instagram (Feed Profilo) ===== */}
      <InstagramProfilePreview status={status} contents={contents} publications={publications} />

      {/* ===== Anteprima tipo di carosello ===== */}
      <CaroselloPreviewDemo />

      {/* ===== Storico pubblicazioni ===== */}
      {publications.length > 0 && (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="ig-history">
          <h3 className="font-heading text-lg font-bold text-zinc-900 mb-4">Storico pubblicazioni</h3>
          <div className="space-y-2">
            {publications.slice(0, 20).map(p => (
              <div key={p.id || p.media_id} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                <div className={`w-2 h-2 rounded-full ${p.status === "published" ? "bg-green-500" : "bg-red-500"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{p.caption?.slice(0, 80) || "(senza didascalia)"}</p>
                  <p className="text-xs text-zinc-400">
                    {p.status === "published" ? "Pubblicato" : "Fallito"} — {new Date(p.published_at || p.attempted_at).toLocaleString("it-IT")}
                    {p.media_id && <span className="ml-2 font-mono">#{p.media_id}</span>}
                  </p>
                  {p.error && <p className="text-xs text-red-600 mt-1">{p.error.slice(0, 150)}</p>}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold shrink-0">{(p.image_urls || []).length} img</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
